/**
 * Utility for persisting audio files in IndexedDB to bypass browser memory/session limits.
 */
class FileStorage {
    constructor() {
        this.DB_NAME = 'TranswasmFileCache';
        this.AUDIO_STORE = 'audio_files';
        this.ANALYSIS_STORE = 'analysis_results';
        this.METADATA_STORE = 'cache_metadata';
        this.VERSION = 3;
        this.MAX_FILES = 3;
        this.db = null;
    }

    async init() {
        if (this.db) return this.db;
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(this.DB_NAME, this.VERSION);
            request.onupgradeneeded = (e) => {
                const db = e.target.result;
                if (!db.objectStoreNames.contains(this.AUDIO_STORE)) {
                    db.createObjectStore(this.AUDIO_STORE);
                }
                if (!db.objectStoreNames.contains(this.ANALYSIS_STORE)) {
                    db.createObjectStore(this.ANALYSIS_STORE);
                }
                if (!db.objectStoreNames.contains(this.METADATA_STORE)) {
                    db.createObjectStore(this.METADATA_STORE);
                }
            };
            request.onsuccess = (e) => {
                this.db = e.target.result;
                resolve(this.db);
            };
            request.onerror = (e) => reject(e.target.error);
        });
    }

    /**
     * Saves a file (Blob or ArrayBuffer) associated with a hash.
     */
    async saveFile(hash, data) {
        const db = await this.init();
        return new Promise((resolve, reject) => {
            const transaction = db.transaction([this.AUDIO_STORE, this.METADATA_STORE, this.ANALYSIS_STORE], 'readwrite');
            const audioStore = transaction.objectStore(this.AUDIO_STORE);
            const metaStore = transaction.objectStore(this.METADATA_STORE);

            audioStore.put(data, hash);
            metaStore.put({ lastUsed: Date.now() }, hash);

            this._enforceLimit(transaction).then(resolve).catch(reject);
        });
    }

    /**
     * Saves analysis results associated with a hash.
     */
    async saveAnalysis(hash, data) {
        const db = await this.init();
        return new Promise((resolve, reject) => {
            const transaction = db.transaction([this.ANALYSIS_STORE, this.METADATA_STORE, this.AUDIO_STORE], 'readwrite');
            const analysisStore = transaction.objectStore(this.ANALYSIS_STORE);
            const metaStore = transaction.objectStore(this.METADATA_STORE);

            analysisStore.put(data, hash);
            metaStore.put({ lastUsed: Date.now() }, hash);

            this._enforceLimit(transaction).then(resolve).catch(reject);
        });
    }

    /**
     * Retrieves a file by its hash.
     */
    async getFile(hash) {
        const db = await this.init();
        return new Promise((resolve, reject) => {
            const transaction = db.transaction([this.AUDIO_STORE, this.METADATA_STORE], 'readwrite');
            const store = transaction.objectStore(this.AUDIO_STORE);
            const metaStore = transaction.objectStore(this.METADATA_STORE);

            const request = store.get(hash);
            request.onsuccess = (e) => {
                if (e.target.result) {
                    metaStore.put({ lastUsed: Date.now() }, hash);
                }
                resolve(e.target.result);
            };
            request.onerror = (e) => reject(e.target.error);
        });
    }

    /**
     * Retrieves analysis results by hash.
     */
    async getAnalysis(hash) {
        const db = await this.init();
        return new Promise((resolve, reject) => {
            const transaction = db.transaction([this.ANALYSIS_STORE, this.METADATA_STORE], 'readwrite');
            const store = transaction.objectStore(this.ANALYSIS_STORE);
            const metaStore = transaction.objectStore(this.METADATA_STORE);

            const request = store.get(hash);
            request.onsuccess = (e) => {
                if (e.target.result) {
                    metaStore.put({ lastUsed: Date.now() }, hash);
                }
                resolve(e.target.result);
            };
            request.onerror = (e) => reject(e.target.error);
        });
    }

    /**
     * Clears all cached files and analysis.
     */
    async clear() {
        const db = await this.init();
        return new Promise((resolve, reject) => {
            const transaction = db.transaction([this.AUDIO_STORE, this.ANALYSIS_STORE, this.METADATA_STORE], 'readwrite');
            transaction.objectStore(this.AUDIO_STORE).clear();
            transaction.objectStore(this.ANALYSIS_STORE).clear();
            transaction.objectStore(this.METADATA_STORE).clear();
            transaction.oncomplete = () => resolve();
            transaction.onerror = (e) => reject(e.target.error);
        });
    }

    /**
     * Internal helper to enforce the cache limit.
     * @private
     */
    async _enforceLimit(transaction) {
        return new Promise((resolve, reject) => {
            const metaStore = transaction.objectStore(this.METADATA_STORE);
            const request = metaStore.openCursor();
            const entries = [];

            request.onsuccess = (e) => {
                const cursor = e.target.result;
                if (cursor) {
                    entries.push({ hash: cursor.key, lastUsed: cursor.value.lastUsed });
                    cursor.continue();
                } else {
                    if (entries.length <= this.MAX_FILES) {
                        resolve();
                        return;
                    }

                    // Sort by lastUsed ascending (oldest first)
                    entries.sort((a, b) => a.lastUsed - b.lastUsed);

                    const toDelete = entries.slice(0, entries.length - this.MAX_FILES);
                    const audioStore = transaction.objectStore(this.AUDIO_STORE);
                    const analysisStore = transaction.objectStore(this.ANALYSIS_STORE);

                    toDelete.forEach(entry => {
                        metaStore.delete(entry.hash);
                        audioStore.delete(entry.hash);
                        analysisStore.delete(entry.hash);
                    });
                    resolve();
                }
            };
            request.onerror = (e) => reject(e.target.error);
        });
    }

    /**
     * Retrieves a list of all file hashes currently in the cache.
     */
    async getCachedHashes() {
        const db = await this.init();
        return new Promise((resolve, reject) => {
            const transaction = db.transaction([this.METADATA_STORE], 'readonly');
            const store = transaction.objectStore(this.METADATA_STORE);
            const request = store.getAllKeys();

            request.onsuccess = (e) => {
                resolve(e.target.result || []);
            };
            request.onerror = (e) => reject(e.target.error);
        });
    }
}

export const fileStorage = new FileStorage();
